import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { maskPhoneBR, sanitizePhone } from "../lib/phone";
import {
  maskCPF, isValidCPF, sanitizeDigits, maskCEP, isValidCEP,
  fetchAddressByCEP, maskRG, BR_STATES,
} from "../lib/documents";

const INPUT_CLS =
  "w-full mt-2 bg-white text-[#0A1128] border border-gray-200 rounded-md px-4 py-2.5 outline-none focus:border-[#14213D] placeholder:text-gray-400";
const LBL_CLS = "text-xs uppercase tracking-[0.12em] text-gray-500 font-semibold";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    cpf: "", rg: "",
    cep: "", street: "", number: "", complement: "",
    neighborhood: "", city: "", state: "",
  });
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onCepBlur = async () => {
    if (!isValidCEP(form.cep)) return;
    setCepLoading(true);
    const addr = await fetchAddressByCEP(form.cep);
    setCepLoading(false);
    if (addr) {
      update({
        street: addr.street || form.street,
        neighborhood: addr.neighborhood || form.neighborhood,
        city: addr.city || form.city,
        state: addr.state || form.state,
      });
    } else {
      toast.warning("CEP não encontrado — preencha os campos manualmente.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    // Client-side validations
    if (!isValidCPF(form.cpf)) return toast.error("CPF inválido — verifique os dígitos.");
    if (!isValidCEP(form.cep)) return toast.error("CEP inválido — 8 dígitos.");
    if (!form.state || form.state.length !== 2) return toast.error("Selecione o estado.");

    setLoading(true);
    const res = await register({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
      phone: sanitizePhone(form.phone),
      cpf: sanitizeDigits(form.cpf),
      rg: form.rg.trim(),
      address: {
        cep: sanitizeDigits(form.cep),
        street: form.street.trim(),
        number: form.number.trim(),
        complement: form.complement.trim(),
        neighborhood: form.neighborhood.trim(),
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
      },
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Conta criada com sucesso!");
      nav("/portal");
    } else {
      toast.error(res.error || "Não foi possível criar a conta.");
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative theme-marketing overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1497366412874-3415097a27e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2V8ZW58MHx8fHwxNzc4NTc1Mzk2fDA&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#0A1128]/95 to-[#14213D]/90" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md border border-[#D4AF37]/40 flex items-center justify-center">
              <span className="font-display font-bold text-[#D4AF37] text-lg">VM</span>
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-white text-base">VM Corporate</div>
              <div className="text-[10px] text-white/50 uppercase tracking-[0.2em]">Coworking Premium</div>
            </div>
          </Link>
          <div>
            <h2 className="font-display text-4xl text-white font-light leading-tight">
              Bem-vindo à
              <span className="block italic text-[#D4AF37] mt-2">comunidade VM.</span>
            </h2>
            <p className="text-white/60 mt-4 max-w-md">
              Crie sua conta e tenha acesso ao portal, planos exclusivos e reservas online.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 bg-white flex items-center justify-center p-6 sm:p-8 overflow-y-auto">
        <div className="w-full max-w-2xl py-8">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-md bg-[#14213D] flex items-center justify-center">
              <span className="font-display font-bold text-[#D4AF37] text-sm">VM</span>
            </div>
            <div className="font-display font-semibold text-[#14213D]">VM Corporate</div>
          </Link>

          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3">Cadastro</div>
          <h1 className="font-display text-3xl text-[#14213D] font-light mb-6">Criar conta</h1>

          <form onSubmit={submit} className="space-y-5">
            {/* Identificação */}
            <section className="space-y-4">
              <h2 className="text-sm font-bold uppercase tracking-widest text-gray-700">Dados pessoais</h2>
              <div>
                <label className={LBL_CLS}>Nome completo *</label>
                <input required autoComplete="name" value={form.name}
                  onChange={(e) => update({ name: e.target.value })}
                  className={INPUT_CLS} data-testid="reg-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={LBL_CLS}>E-mail *</label>
                  <input required type="email" autoComplete="email" value={form.email}
                    onChange={(e) => update({ email: e.target.value })}
                    className={INPUT_CLS} data-testid="reg-email" />
                </div>
                <div>
                  <label className={LBL_CLS}>Celular *</label>
                  <input required type="tel" inputMode="tel" autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => update({ phone: maskPhoneBR(e.target.value) })}
                    placeholder="(21) 99999-0000"
                    className={INPUT_CLS} data-testid="reg-phone" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={LBL_CLS}>CPF *</label>
                  <input required value={form.cpf}
                    onChange={(e) => update({ cpf: maskCPF(e.target.value) })}
                    placeholder="000.000.000-00" inputMode="numeric"
                    className={INPUT_CLS} data-testid="reg-cpf" />
                </div>
                <div>
                  <label className={LBL_CLS}>RG *</label>
                  <input required value={form.rg}
                    onChange={(e) => update({ rg: maskRG(e.target.value) })}
                    placeholder="00.000.000-0"
                    className={INPUT_CLS} data-testid="reg-rg" />
                </div>
              </div>
            </section>

            {/* Endereço */}
            <section className="space-y-4 pt-2">
              <h2 className="text-sm font-bold uppercase tracking-widest text-gray-700">Endereço</h2>
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
                <div>
                  <label className={LBL_CLS}>CEP *</label>
                  <div className="relative">
                    <input required value={form.cep}
                      onChange={(e) => update({ cep: maskCEP(e.target.value) })}
                      onBlur={onCepBlur}
                      placeholder="00000-000" inputMode="numeric"
                      className={INPUT_CLS} data-testid="reg-cep" />
                    {cepLoading && (
                      <Loader2 size={16} className="animate-spin absolute right-3 top-1/2 mt-1 -translate-y-1/2 text-gray-400" />
                    )}
                  </div>
                </div>
                <div>
                  <label className={LBL_CLS}>Rua *</label>
                  <input required value={form.street}
                    onChange={(e) => update({ street: e.target.value })}
                    className={INPUT_CLS} data-testid="reg-street" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3">
                <div>
                  <label className={LBL_CLS}>Número *</label>
                  <input required value={form.number}
                    onChange={(e) => update({ number: e.target.value })}
                    inputMode="numeric" className={INPUT_CLS} data-testid="reg-number" />
                </div>
                <div>
                  <label className={LBL_CLS}>Complemento</label>
                  <input value={form.complement}
                    onChange={(e) => update({ complement: e.target.value })}
                    placeholder="Apto, bloco, ponto de referência"
                    className={INPUT_CLS} data-testid="reg-complement" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={LBL_CLS}>Bairro *</label>
                  <input required value={form.neighborhood}
                    onChange={(e) => update({ neighborhood: e.target.value })}
                    className={INPUT_CLS} data-testid="reg-neighborhood" />
                </div>
                <div>
                  <label className={LBL_CLS}>Cidade *</label>
                  <input required value={form.city}
                    onChange={(e) => update({ city: e.target.value })}
                    className={INPUT_CLS} data-testid="reg-city" />
                </div>
                <div>
                  <label className={LBL_CLS}>Estado *</label>
                  <select required value={form.state}
                    onChange={(e) => update({ state: e.target.value })}
                    className={INPUT_CLS} data-testid="reg-state">
                    <option value="">UF</option>
                    {BR_STATES.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Senha */}
            <section className="space-y-2 pt-2">
              <h2 className="text-sm font-bold uppercase tracking-widest text-gray-700">Acesso</h2>
              <label className={LBL_CLS}>Senha *</label>
              <div className="relative mt-2">
                <input required type={showPassword ? "text" : "password"} minLength={6}
                  value={form.password} autoComplete="new-password"
                  onChange={(e) => update({ password: e.target.value })}
                  className={`${INPUT_CLS} pr-12`} data-testid="reg-password" />
                <button type="button" onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded text-gray-400 hover:text-[#14213D] hover:bg-gray-100"
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} tabIndex={-1}
                  data-testid="reg-toggle-password">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="text-[11px] text-gray-400 mt-1">Mínimo 6 caracteres</div>
            </section>

            <button type="submit" disabled={loading}
              className="w-full bg-[#14213D] text-white font-semibold py-3 rounded-md hover:bg-[#1F335E] transition disabled:opacity-50 flex items-center justify-center gap-2 mt-6"
              data-testid="reg-submit">
              {loading ? "Criando…" : <>Criar conta <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-gray-500">
            Já tem conta?{" "}
            <Link to="/login" className="text-[#14213D] font-semibold hover:text-[#D4AF37]" data-testid="link-login">
              Entrar
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
